import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { EditIntegrationIncompleteWarningAlert } from '@modules/integrationsV2/components/EditIntegrationDrawerContent/EditIntegrationIncompleteWarningAlert';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IntegrationChannelType } from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';

const SUBREDDIT_PREFIX = 'r/';

export const EditRedditIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationData,
  integrationType,
  description,
  integrationChannel,
}: EditIntegrationDrawerContentProps) => {
  const [subreddit, setSubreddit] = useState(
    integrationData.reddit?.subreddit ?? '',
  );
  const [descriptionInput, setDescriptionInput] = useState(description);

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Reddit,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.Reddit,
          integrationID,
        });
      },
    });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Reddit,
    integrationType,
    hasValidFormFields: !!subreddit,
    descriptionInput,
    currentDescription: description,
    onValidated: async () => {
      updateIntegration({
        integrationID,
        isActive: true,
        updateData: {
          reddit: {
            subreddit,
          },
        },
        updateDescription: {
          description: descriptionInput.trim(),
        },
      });
    },
  });

  const isSubmitting = IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title="Edit Reddit Integration"
        />
        <Box p={3}>
          <EditIntegrationIncompleteWarningAlert
            integrationData={integrationData}
            channelType={IntegrationChannelType.Reddit}
          />
          <Stack
            spacing={4}
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
            autoComplete="off"
          >
            <Stack spacing={2}>
              <Stack>
                <Typography variant="subtitle1">Enter Subreddit</Typography>
                <Typography variant="caption" color="text.secondary">
                  Enter the subreddit you would like Enterpret to get data from
                </Typography>
              </Stack>
              <TextField
                fullWidth
                variant="outlined"
                label="Subreddit"
                value={subreddit}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {SUBREDDIT_PREFIX}
                    </InputAdornment>
                  ),
                }}
                onChange={(event) => setSubreddit(event.target.value)}
              />
            </Stack>
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          </Stack>
        </Box>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        isDisabled={isDisabled}
        isLoading={isSubmitting}
      />
    </Stack>
  );
};
